import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

const GooglePlacesInput = ({
  value,
  onChange,
  placeholder,
  className,
  error,
  disabled = false,
  dir = "rtl",
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  useEffect(() => {
    if (window.google && window.google.maps) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
    }
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    if (inputValue.length > 2 && autocompleteService.current) {
      setIsLoading(true);

      autocompleteService.current.getPlacePredictions(
        {
          input: inputValue,
          componentRestrictions: { country: "il" }, // Restrict to Israel
          types: ["establishment", "geocode"],
        },
        (predictions, status) => {
          setIsLoading(false);
          //console.log("Predictions:", predictions, "Status:", status);
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(predictions.slice(0, 5)); // Limit to 5 suggestions
            setShowSuggestions(true);
            //console.log("Showing suggestions:", predictions.slice(0, 5));
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
      );
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    var place_id = suggestion.place_id;
    if (placesService.current && place_id) {
      placesService.current.getDetails(
        { placeId: place_id },
        (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            var address = place.formatted_address;
            var lat = place.geometry.location.lat();
            var lng = place.geometry.location.lng();
            //console.log("Selected place details:", address, lat, lng);
            onChange(suggestion.description, address, lat, lng);
            setSuggestions([]);
            setShowSuggestions(false);
          } else {
            console.error("Error fetching place details:", status);
          }
        }
      );
    }
    // onChange(suggestion.description);
    // setSuggestions([]);
    // setShowSuggestions(false);
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow click events
    setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  };

  // console.log(
  //   "Render - showSuggestions:",
  //   showSuggestions,
  //   "suggestions:",
  //   suggestions.length
  // );

  return (
    <div className="relative" style={{ overflow: "visible" }}>
      <div className="relative flex items-center">
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5">
          <MapPin className="w-5 h-5" />
        </div>

        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          className={`h-12 rounded-xl border-2 transition-all focus:ring-0 focus:outline-none focus:shadow-none w-full pr-10 ${
            error ? "border-red-300" : "border-gray-200 focus:border-rose-300"
          } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""} ${
            className || ""
          }`}
          style={{
            boxShadow: "none",
            outline: "none",
          }}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          dir={dir}
          disabled={disabled}
        />
      </div>

      {showSuggestions &&
        suggestions.length > 0 &&
        createPortal(
          <div
            className="fixed bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto"
            style={{
              zIndex: 999999,
              top: inputRef.current
                ? inputRef.current.getBoundingClientRect().bottom + 4
                : 0,
              left: inputRef.current
                ? inputRef.current.getBoundingClientRect().left
                : 0,
              width: inputRef.current
                ? inputRef.current.getBoundingClientRect().width
                : 300,
            }}
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.place_id}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 text-right"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0 text-right">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {suggestion.structured_formatting?.main_text ||
                        suggestion.description}
                    </div>
                    {suggestion.structured_formatting?.secondary_text && (
                      <div className="text-xs text-gray-500 truncate">
                        {suggestion.structured_formatting.secondary_text}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>,
          document.body
        )}

      {isLoading && (
        <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-rose-500"></div>
        </div>
      )}
    </div>
  );
};

export default GooglePlacesInput;
